import { CollectionType } from '@/enums/collection-type';
import {
	categoryAliases,
	getSportCategoryMap,
	isSportSupported,
	sportAliases,
	SportRouteTypes,
} from '@/features/sports/types/enums';

import type { SportType } from '@/@types/graphql-types';
import type { SportCategory, SportLocale } from '@/features/sports/types/enums';
import type { WebLocale } from '@/enums/web-locale';
import type { NavigationGuardNext, Route, RouteConfig } from 'vue-router';

// SingleStep Pages
type SingleStepCompetition = any;
type SingleStepTeam = any;

// import type SingleStepTeam from '@/pages/sports/single-step/SingleStepTeam.vue';
import { PageType } from './config';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { Domain } from '@/routing/helpers/subdomain-helper';

/*
	This route config generates all the Sport routes per locale.
	The point was to have a typesafe way to create all routes (thanks to `src/enums/sports.ts`) and
	to expose a `$route.meta.sport` property that can be used in the app.

	With this property, we do not need to wait for data to load before knowing the "objectType" as
	is the case in `TitleDetail` for example.

	We couldn't just use route's dynamic parts like `/de/:sport` and then `$route.params.sport`
	because the routes get translated and we would have too many routes clashing:
	`/${webLocale}/*` -> All sports, each sport, static pages

	For more info see: https://gitlab.justwatch.com/jw/jw-app/-/wikis/Sports-FE-Architecture

	## Example routes for "DE"

	All Sports (app.sports.subdomain.all)
	* /de

	Overview (app.sports.subdomain.overview)
	* /de/fussball
	* /de/motorsport
	* ... for each sport listed in `src/enums/sports.ts:sportAliases`

	Competition (app.sports.subdomain.competition)
	* /de/fussball/:competition
	* /de/motorsport/:competition
	* ... for each sport listed in `src/enums/sports.ts:sportAliases`

	Event (app.sports.subdomain.event)
	* /de/fussball/:competition/:event
	* /de/motorsport/:competition/:event
	* ... for each sport listed in `src/enums/sports.ts:sportAliases`
*/

type beforeEnterArgs<T extends Vue = Vue> = [to: Route, from: Route, next: NavigationGuardNext<T>];
const sportsSubdomainMeta = {
	tab: 'sports',
	collectionType: CollectionType.SPORTS,
	pageType: PageType.SPORTS,
	subdomain: Domain.SPORTS,
};

const beforeEnter = async (_: Route, from: Route, next: NavigationGuardNext) => {
	const languageStore = useLanguageStore();

	// Fetch sports translations and load locales unless navigating within sports subdomain
	if (!from.name?.startsWith('app.sports')) {
		await Promise.all([languageStore.fetchSportsTranslation(), languageStore.fetchLocales()]);
	}
	next();
};

function makeSportsSubdomainLandingRoute(): RouteConfig {
	return {
		path: `/`,
		name: 'app.sports.subdomain.landing',
		component: () => import(/* webpackChunkName: "sports" */ '@/pages/sports/SportsLanding.vue'),
		meta: { ...sportsSubdomainMeta },
		beforeEnter: (...args) => beforeEnter(...args),
	};
}

function makeAllSportsRoute(currentLocale: SportLocale): RouteConfig {
	return {
		name: 'app.sports.subdomain.all',
		path: `/${currentLocale}`,
		component: () => import('@/pages/sports/AllSports.vue'),
		meta: { ...sportsSubdomainMeta, data: 'all' },
		beforeEnter: (...args) => beforeEnter(...args),
	};
}

function makeOverviewRoutes(currentLocale: SportLocale): RouteConfig[] {
	// /uk/{sport} -> /uk/football
	const singleSport = SportRouteTypes.sports
		.filter(sport => sportAliases[sport][currentLocale])
		.map(
			sport =>
				({
					name: 'app.sports.subdomain.overview',
					// @ts-ignore
					path: `/${currentLocale}/${encodeURI(sportAliases[sport][currentLocale])}`,
					component: () => import('@/pages/sports/single-step/SingleStepOverview.vue'),
					meta: { ...sportsSubdomainMeta, data: 'overview.singlestep', sport, contexts: ['filter'] },
					beforeEnter: (...args) => beforeEnter(...args),
				} as RouteConfig)
		);

	// /uk/{category} -> /uk/motorsport
	const sportCategory = (<SportCategory[]>Object.keys(SportRouteTypes.categories))
		.filter(category => sportAliases[category][currentLocale])
		.map(
			category =>
				({
					name: 'app.sports.subdomain.overview',
					// @ts-ignore
					path: `/${currentLocale}/${encodeURI(sportAliases[category][currentLocale])}`,
					component: () => import('@/pages/sports/motorsport/MotorsportOverview.vue'),
					meta: { ...sportsSubdomainMeta, data: 'overview.multistep', sport: category, contexts: ['filter'] },
					beforeEnter: (...args) => beforeEnter(...args),
				} as RouteConfig)
		);

	return [...singleSport, ...sportCategory];
}

function makeMultistepCompetitionRoutes(currentLocale: SportLocale): RouteConfig[] {
	return (<SportCategory[]>Object.keys(SportRouteTypes.categories))
		.filter(category => sportAliases[category][currentLocale])
		.map(
			category =>
				({
					// /uk/{category}/{competition} -> /uk/motorsport/formula-1
					name: 'app.sports.subdomain.competition',
					// @ts-ignore
					path: `/${currentLocale}/${encodeURI(sportAliases[category][currentLocale])}/:competition`,
					component: () => import('@/pages/sports/motorsport/MotorsportCompetition.vue'),
					meta: {
						...sportsSubdomainMeta,
						data: 'competition.multistep',
						sport: category,
						contexts: ['filter'],
					},
					beforeEnter: (...args) => beforeEnter(...args),
				} as RouteConfig)
		)
		.flat();
}

function makeTeamOrCompetitionRoutes(currentLocale: SportLocale): RouteConfig[] {
	return SportRouteTypes.sports
		.filter(sports => sportAliases[sports][currentLocale])
		.map(
			sport =>
				({
					// /uk/{sport}/{team} -> /uk/football/manchester-united
					name: 'app.sports.subdomain.team|competition',
					// @ts-ignore
					path: `/${currentLocale}/${encodeURI(sportAliases[sport][currentLocale])}/:team`,
					component: () => import('@/pages/sports/single-step/SingleStepTeamOrCompetition.vue'),
					meta: { ...sportsSubdomainMeta, data: 'team|competition.singlestep', sport, contexts: ['filter'] },
					beforeEnter: (...args: beforeEnterArgs<SingleStepTeam | SingleStepCompetition>) =>
						beforeEnter(...args),
				} as RouteConfig)
		);
}

function makeEventRoutes(currentLocale: SportLocale): RouteConfig[] {
	// /uk/{sport}/{competition}/{eventName} -> /uk/tennis/french-open-men-singles/pablo-carreno-busta-gilles-simon-2022
	const singleSport = SportRouteTypes.sports
		.filter(sports => sportAliases[sports][currentLocale])
		.map(
			sport =>
				({
					name: 'app.sports.subdomain.event',
					// @ts-ignore
					path: `/${currentLocale}/${encodeURI(sportAliases[sport][currentLocale])}/:competition/:event`,
					component: () => import('@/pages/sports/single-step/SingleStepEvent.vue'),
					meta: { ...sportsSubdomainMeta, data: 'event.singlestep', sport },
					beforeEnter: (...args) => beforeEnter(...args),
				} as RouteConfig)
		);

	// /uk/{category}/{competition}/{event} -> /uk/motorsport/formula-1/dutch-grand-prix-2022
	const sportCategory = (<SportCategory[]>Object.keys(SportRouteTypes.categories))
		.filter(category => sportAliases[category][currentLocale])
		.map(category => {
			return {
				name: 'app.sports.subdomain.event',
				// @ts-ignore
				path: `/${currentLocale}/${encodeURI(sportAliases[category][currentLocale])}/:competition/:event`,
				component: () => import('@/pages/sports/motorsport/MotorsportEvent.vue'),
				meta: { ...sportsSubdomainMeta, data: 'event.multistep', sport: category },
				beforeEnter: (...args) => beforeEnter(...args),
			} as RouteConfig;
		})
		.flat();

	return [...singleSport, ...sportCategory];
}

export function makeSportRoutes(locale: WebLocale) {
	if (!isSportSupported(locale)) {
		return [makeSportsSubdomainLandingRoute()];
	}

	return [
		makeSportsSubdomainLandingRoute(),
		makeAllSportsRoute(locale),
		...makeOverviewRoutes(locale),
		...makeTeamOrCompetitionRoutes(locale),
		...makeMultistepCompetitionRoutes(locale),
		...makeEventRoutes(locale),
	];
}

/**
 * Get the URL to a sport or category's overview page. It's simple for
 * non category sports like football but more complicated for categories
 * and their competitions (i.e. Motorsport and Formula 1).
 *
 * @param sport The sport or category for which you want the URL
 * @param currentLocale
 * @returns The URL to the given sport's overview page
 */
export function getOverviewUrlBySport(sport: SportType | SportCategory, currentLocale: SportLocale) {
	if (SportRouteTypes.sports.includes(sport as SportType)) {
		return `/${currentLocale}/${sportAliases[sport][currentLocale]}`;
	}

	if (SportRouteTypes.categories[sport as SportCategory] != null) {
		return `/${currentLocale}/${sportAliases[sport][currentLocale]}`;
	}

	const category = getSportCategoryMap()[sport as SportType];
	if (category == null) return '';

	// @ts-ignore at this point sport would only be one of the category sports
	const motorsportOverview = categoryAliases[category][sport][currentLocale].overview;
	return `/${currentLocale}/${sportAliases[category][currentLocale]}/${motorsportOverview}`;
}
